import { useEffect, useState } from 'react';
import { AuthenticatorType, UpdateAuthenticatorResult } from '@mews-commander/data-access';
import { LocalizedSmallParagraph, useDialogs } from '@mews-commander/ui';
import { FormInputField, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Dialog, Link, Stack } from '@optimus-web/core';
import { useCurrentUser } from '@mews-commander/core';
import { Configure2FAPanelRoutes, use2FAEmailCode, useConfigure2FAPanel } from '../../../../auth';
import { useAuthenticatorCodeForm } from './AuthenticatorCodeForm';

export const MFA_RESET_DIALOG_NAME = 'resetMFA';
const FORM_ID = 'reset2faForm';

const useReset2FAForm = (password: string, onSuccess?: (data: UpdateAuthenticatorResult) => void) =>
    useAuthenticatorCodeForm({
        id: FORM_ID,
        onSuccess,
        params: {
            password,
            authenticator: AuthenticatorType.None,
        },
    });

const authenticatorTextKeyMap = {
    [AuthenticatorType.Google]: { label: TextKey.VerifyIdentity, description: TextKey.VerifyIdentityInstructions },
    [AuthenticatorType.Email]: {
        label: TextKey.TwoFactorAuthenticationVerifyEmailLabel,
        description: TextKey.TwoFactorAuthenticationVerifyEmailDescription,
    },
    [AuthenticatorType.None]: { label: TextKey.FatalErrorMessage, description: TextKey.FatalErrorMessage },
};

export const MFAResetDialog = ({ password }: { password: string }) => {
    const { open2FAPanel } = useConfigure2FAPanel();
    const { closeDialog } = useDialogs();
    const { authenticator } = useCurrentUser();
    // We need to store the authenticator state on mount to ensure that re-route occurs correctly after successful reset
    // Currently, in order to change the authentication method you need to disable it first
    // This leads to a race condition in which the authenticator variable gets updated and the submit logic cannot determine the correct route
    const [initialAuthenticator] = useState(authenticator);
    const { sendTwoFactorCode, reSendTwoFactorCode } = use2FAEmailCode();

    const handleClose = () => closeDialog(MFA_RESET_DIALOG_NAME);
    const handleSuccess = () => {
        handleClose();
        open2FAPanel(
            initialAuthenticator === AuthenticatorType.Google
                ? Configure2FAPanelRoutes.emailConfig
                : Configure2FAPanelRoutes.deviceConfig,
            password
        );
    };

    const { renderFormContent, isLoading } = useReset2FAForm(password, handleSuccess);

    useEffect(() => {
        if (authenticator === AuthenticatorType.Email) {
            sendTwoFactorCode();
        }
    }, [authenticator, sendTwoFactorCode]);

    return (
        <Dialog
            active
            size="small"
            title={<LocalizedText textKey={authenticatorTextKeyMap[authenticator].label} />}
            loading={isLoading}
            onClose={handleClose}
            footer={{
                primary: (
                    <Button variant="primary" type="submit" form={FORM_ID}>
                        <LocalizedText textKey={TextKey.Verify} />
                    </Button>
                ),
                secondary: (
                    <Button variant="tertiary" type="button" onClick={handleClose}>
                        <LocalizedText textKey={TextKey.Cancel} />
                    </Button>
                ),
            }}
        >
            {renderFormContent(
                <Stack vertical spacing="200">
                    <Stack vertical spacing="100">
                        <LocalizedSmallParagraph
                            textKey={authenticatorTextKeyMap[authenticator].description}
                            variant="secondary"
                        />
                        {authenticator === AuthenticatorType.Email && (
                            <Link standalone onClick={reSendTwoFactorCode} size="small">
                                <LocalizedText textKey={TextKey.ResendEmail} />
                            </Link>
                        )}
                    </Stack>

                    <FormInputField
                        name="pin"
                        size="medium"
                        required
                        label={<LocalizedText textKey={TextKey.AuthenticationCode} />}
                    />
                </Stack>
            )}
        </Dialog>
    );
};
